import { useCallback, useEffect, useMemo } from "react";

import { debounce } from "lodash";

type Options = {
  ref: React.RefObject<HTMLElement>;
  targetRef?: React.RefObject<HTMLElement>;
  bottomGutterHeight?: number;
};

export const useRemainingVerticalHeight = ({
  ref,
  targetRef,
  bottomGutterHeight = 16,
}: Options) => {
  const resize = useCallback(() => {
    if (!ref.current) {
      return;
    }
    const rect = ref.current.getBoundingClientRect();
    const targetHeight = targetRef?.current?.getBoundingClientRect().height;
    const value = targetHeight
      ? `${targetHeight}px`
      : `calc(100vh - ${rect.top + bottomGutterHeight}px)`;
    ref.current.style.height = value;
  }, [bottomGutterHeight, ref, targetRef]);

  const debouncedCallback = useMemo(() => debounce(resize, 25), [resize]);

  useEffect(() => {
    if (!ref.current) {
      return;
    }

    const observer = new ResizeObserver((entries: ResizeObserverEntry[]) => {
      if (!Array.isArray(entries) || !entries.length) return;
      debouncedCallback();
    });
    observer.observe(ref.current);

    return () => {
      observer.disconnect();
    };
  }, [debouncedCallback, ref]);

  useEffect(() => {
    window.addEventListener("scrollend", debouncedCallback);
    return () => {
      window.removeEventListener("scrollend", debouncedCallback);
    };
  }, [debouncedCallback]);

  return { resize: debouncedCallback };
};
